import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { setActiveGroupId, setAuthToken, getApiBaseUrl } from '../../services/api';
import { emitDataChange, onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import './Settings.css';
import AppIcon from '../common/AppIcon';

const Settings = () => {
  const [hospitals, setHospitals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [systemStats, setSystemStats] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [dataManagementLoading, setDataManagementLoading] = useState(false);
  // UI display settings (persisted locally)
  const [theme, setTheme] = useState(localStorage.getItem('ui_theme') || 'light');
  const [calendarView, setCalendarView] = useState(localStorage.getItem('ui_calendar_view') || 'month');
  const [showWeekends, setShowWeekends] = useState((localStorage.getItem('ui_show_weekends') || 'true') === 'true');
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroup] = useState(localStorage.getItem('activeGroupId') || '1');
  const [newGroup, setNewGroup] = useState({ slug: '', name: '' });

  // Auth for admin-only endpoints (groups management)
  const [authRequired, setAuthRequired] = useState(!localStorage.getItem('authToken'));
  const [authLoading, setAuthLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: 'admin', password: '' });

  const apiUrl = getApiBaseUrl();

  const handleLogin = async (e) => {
    e.preventDefault();
    const username = (loginForm.username || '').trim();
    const password = (loginForm.password || '').trim();
    if (!username || !password) return;
    try {
      setAuthLoading(true);
      const res = await axios.post(`${apiUrl}/api/auth/login`, { username, password });
      const token = res?.data?.access_token;
      if (!token) throw new Error('No access_token returned');
      setAuthToken(token);
      setAuthRequired(false);
      // If backend returned a group id, align frontend group selection.
      if (res?.data?.group_id) {
        setActiveGroupId(String(res.data.group_id));
        setActiveGroup(String(res.data.group_id));
      }
      await loadGroups();
      await loadData();
      emitDataChange(DATA_TYPES.ALL);
    } catch (err) {
      console.error('Login failed:', err);
      alert(err?.response?.data?.detail || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const loadGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/groups`);
      const all = Array.isArray(res.data) ? res.data : [];
      setGroups(all.filter((g) => g?.is_active !== false));
      setAuthRequired(false);
    } catch (e) {
      console.error('Error loading groups:', e);
      setGroups([]);
      // If the backend returns 401 OR the request fails (CORS/network), show login UI.
      if (e?.response?.status === 401 || !localStorage.getItem('authToken')) setAuthRequired(true);
    }
  }, [apiUrl]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.slug.trim() || !newGroup.name.trim()) return;
    try {
      await axios.post(`${apiUrl}/api/groups`, { slug: newGroup.slug.trim(), name: newGroup.name.trim() });
      setNewGroup({ slug: '', name: '' });
      await loadGroups();
    } catch (e) {
      console.error('Error creating group:', e);
      alert(e?.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleSelectGroup = async (gid) => {
    setActiveGroup(String(gid));
    setActiveGroupId(String(gid));
    await loadData();
    emitDataChange(DATA_TYPES.GROUP);
  };

  const handleRenameGroup = async () => {
    const g = (groups || []).find((x) => String(x.id) === String(activeGroupId));
    if (!g) return;
    const newName = window.prompt('Enter new group name', g.name);
    if (!newName || !newName.trim()) return;
    try {
      await axios.put(`${apiUrl}/api/groups/${g.id}`, { name: newName.trim(), slug: g.slug });
      await loadGroups();
    } catch (e) {
      console.error('Error renaming group:', e);
      alert(e?.response?.data?.detail || 'Failed to rename group');
    }
  };

  const handleDeleteGroup = async () => {
    const g = (groups || []).find((x) => String(x.id) === String(activeGroupId));
    if (!g) return;
    if (String(g.id) === '1') return alert('Default group cannot be deleted');
    if (!window.confirm(`Deactivate group "${g.name}"? Data will be hidden but not removed.`)) return;
    try {
      await axios.delete(`${apiUrl}/api/groups/${g.id}`);
      // fall back to default group
      await handleSelectGroup('1');
      await loadGroups();
    } catch (e) {
      console.error('Error deleting group:', e);
      alert(e?.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleDeleteSchedulesForGroup = async () => {
    const g = (groups || []).find((x) => String(x.id) === String(activeGroupId));
    if (!g) return;
    if (!window.confirm(`Delete ALL schedules for "${g.name}"? This cannot be undone.`)) return;
    try {
      await axios.post(`${apiUrl}/api/groups/${g.id}/purge/schedules`);
      await loadData();
      alert('Schedules deleted for this group.');
    } catch (e) {
      console.error('Error deleting schedules for group:', e);
      alert(e?.response?.data?.detail || 'Failed to delete schedules');
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all data from backend — use allSettled so one failure doesn't block others
      const [hospitalsRes, shiftsRes, statusRes, doctorsRes, exportRes] = await Promise.allSettled([
        axios.get(`${apiUrl}/api/hospitals`),
        axios.get(`${apiUrl}/api/processedresult`),
        axios.get(`${apiUrl}/api/status`),
        axios.get(`${apiUrl}/api/doctors`),
        axios.get(`${apiUrl}/api/export?format=json`),
      ]);
      
      const hospitalsData = hospitalsRes.status === 'fulfilled' && Array.isArray(hospitalsRes.value.data)
        ? hospitalsRes.value.data : [];
      const shiftsData = shiftsRes.status === 'fulfilled' && Array.isArray(shiftsRes.value.data)
        ? shiftsRes.value.data : [];
      
      // Try /api/doctors first, fall back to /api/export then shift-derived names
      let doctorsData = [];
      if (doctorsRes.status === 'fulfilled' && Array.isArray(doctorsRes.value.data)) {
        doctorsData = doctorsRes.value.data;
      } else if (exportRes.status === 'fulfilled' && Array.isArray(exportRes.value.data?.doctors)) {
        doctorsData = exportRes.value.data.doctors;
      }
      
      setHospitals(hospitalsData);
      setShifts(shiftsData);
      // Merge registered doctors with shift-derived doctors for full visibility
      const shiftDoctorNames = new Set(shiftsData.map(s => s?.name).filter(Boolean));
      const allDoctors = [...doctorsData];
      shiftDoctorNames.forEach(name => {
        if (!allDoctors.find(d => d.name === name)) {
          allDoctors.push({ name });
        }
      });
      setDoctors(allDoctors);
      
      // Calculate system statistics
      setSystemStats({
        totalHospitals: hospitalsData.length,
        totalDoctors: allDoctors.length,
        totalShifts: shiftsData.length,
        activeLocations: new Set(shiftsData.map(shift => shift.location)).size,
        apiStatus: (statusRes.status === 'fulfilled' && statusRes.value?.data?.status) ? statusRes.value.data.status : 'Online',
        version: '2.0.0'
      });
      
    } catch (e) {
      console.error('Error loading settings data:', e);
      if (e?.response?.status === 401) {
        setAuthRequired(true);
        setError(null);
        return;
      }
      setError('Failed to load system data');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const loadBackups = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/backups`);
      setBackups(Array.isArray(response?.data?.backups) ? response.data.backups : []);
    } catch (error) {
      console.error('Error loading backups:', error);
      setBackups([]);
    }
  }, [apiUrl]);

  useEffect(() => {
    loadData();
    loadBackups();
    loadGroups();
    return onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.SHIFTS, DATA_TYPES.HOSPITALS, DATA_TYPES.DOCTORS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        loadData();
      }
    });
  }, [loadData, loadBackups, loadGroups]);

  useEffect(() => {
    localStorage.setItem('ui_theme', theme);
    if (theme === 'dark') document.body.classList.add('theme-dark');
    else document.body.classList.remove('theme-dark');
    window.dispatchEvent(new Event('uiSettingsChanged'));
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('ui_calendar_view', calendarView);
    window.dispatchEvent(new Event('uiSettingsChanged'));
  }, [calendarView]);

  useEffect(() => {
    localStorage.setItem('ui_show_weekends', String(showWeekends));
    window.dispatchEvent(new Event('uiSettingsChanged'));
  }, [showWeekends]);



  // Generate Word Document
  const generateWordDocument = () => {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "Doctor Schedule Management System - Data Export",
                bold: true,
                size: 32,
              }),
            ],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Generated on: ${new Date().toLocaleDateString()}`,
                size: 24,
              }),
            ],
          }),
          new Paragraph({ children: [new TextRun({ text: "" })] }), // Empty line
          
          // System Statistics
          new Paragraph({
            children: [
              new TextRun({
                text: "System Statistics",
                bold: true,
                size: 28,
              }),
            ],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Total Hospitals: ${systemStats.totalHospitals}`,
                size: 24,
              }),
            ],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Total Doctors: ${systemStats.totalDoctors}`,
                size: 24,
              }),
            ],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Total Shifts: ${systemStats.totalShifts}`,
                size: 24,
              }),
            ],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Active Locations: ${systemStats.activeLocations}`,
                size: 24,
              }),
            ],
          }),
          new Paragraph({ children: [new TextRun({ text: "" })] }), // Empty line
          
          // Hospitals Table
          new Paragraph({
            children: [
              new TextRun({
                text: "Hospitals",
                bold: true,
                size: 28,
              }),
            ],
          }),
          new Table({
            width: {
              size: 100,
              type: WidthType.PERCENTAGE,
            },
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Code", bold: true })] })],
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Name", bold: true })] })],
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Capacity", bold: true })] })],
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Monthly Slots", bold: true })] })],
                  }),
                ],
              }),
              ...hospitals.map(hospital => 
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: hospital.code })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: hospital.name })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: hospital.capacity?.toString() || 'N/A' })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: hospital.monthlySlots?.toString() || 'N/A' })] })],
                    }),
                  ],
                })
              ),
            ],
          }),
          new Paragraph({ children: [new TextRun({ text: "" })] }), // Empty line
          
          // Recent Shifts
          new Paragraph({
            children: [
              new TextRun({
                text: "Recent Shifts (Last 10)",
                bold: true,
                size: 28,
              }),
            ],
          }),
          new Table({
            width: {
              size: 100,
              type: WidthType.PERCENTAGE,
            },
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Doctor", bold: true })] })],
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Location", bold: true })] })],
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Date", bold: true })] })],
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: "Time", bold: true })] })],
                  }),
                ],
              }),
              ...shifts.slice(0, 10).map(shift => 
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: shift.name || 'N/A' })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: shift.location || 'N/A' })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: shift.shift_date || `${shift.shift_day}/${shift.shift_month}/${shift.shift_year}` || 'N/A' })] })],
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: Array.isArray(shift.shift_time) ? `${shift.shift_time[0]}:${shift.shift_time[1]?.toString().padStart(2, '0')}` : '24:00' })] })],
                    }),
                  ],
                })
              ),
            ],
          }),
        ],
      }],
    });

    return doc;
  };

  // Data Management Functions
  const handleExport = async (format) => {
    try {
      setDataManagementLoading(true);
      
      if (format === 'docx') {
        // Generate Word document
        const doc = generateWordDocument();
        const buffer = await Packer.toBuffer(doc);
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        saveAs(blob, `doctor-schedule-export-${new Date().toISOString().split('T')[0]}.docx`);
        alert('Data exported successfully as Word document');
      } else {
        // Handle other formats (CSV, JSON, PDF)
        const response = await axios.get(`${apiUrl}/api/export?format=${format}`, {
          responseType: format === 'json' ? 'blob' : 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `doctor-schedule-export-${new Date().toISOString().split('T')[0]}.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        alert(`Data exported successfully as ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setDataManagementLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setDataManagementLoading(true);
      const response = await axios.post(`${apiUrl}/api/backup`);
      alert(`Backup created successfully: ${response.data.backupFile}`);
      await loadBackups(); // Refresh backup list
    } catch (error) {
      console.error('Backup error:', error);
      alert('Failed to create backup. Please try again.');
    } finally {
      setDataManagementLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (backups.length === 0) {
      alert('No backups available to restore.');
      return;
    }
    
    const backupFile = prompt('Enter backup filename to restore:', backups[0]?.filename);
    if (!backupFile) return;
    
    if (!window.confirm(`Are you sure you want to restore from ${backupFile}? This will overwrite all current data.`)) {
      return;
    }

    try {
      setDataManagementLoading(true);
      const response = await axios.post(`${apiUrl}/api/restore`, { backupFile });
      alert(`Backup restored successfully from ${response.data.restoredFrom}`);
      await loadData();
      emitDataChange(DATA_TYPES.ALL);
    } catch (error) {
      console.error('Restore error:', error);
      alert('Failed to restore backup. Please try again.');
    } finally {
      setDataManagementLoading(false);
    }
  };

  const handleCleanOldShifts = async () => {
    const daysOld = prompt('Enter number of days old to clean (default: 90):', '90');
    if (!daysOld) return;
    
    if (!window.confirm(`Are you sure you want to delete shifts older than ${daysOld} days?`)) {
      return;
    }

    try {
      setDataManagementLoading(true);
              const response = await axios.post(`${apiUrl}/api/cleanup/shifts`, { daysOld: parseInt(daysOld) });
      alert(`Cleaned ${response.data.deletedCount} old shifts`);
      await loadData();
      emitDataChange(DATA_TYPES.SHIFTS);
    } catch (error) {
      console.error('Cleanup error:', error);
      alert('Failed to clean old shifts. Please try again.');
    } finally {
      setDataManagementLoading(false);
    }
  };

  const handleOptimizeDatabase = async () => {
    try {
      setDataManagementLoading(true);
      const response = await axios.post(`${apiUrl}/api/optimize`);
      alert(`Database optimization completed. ${response.data.stats.totalShifts} shifts, ${response.data.stats.totalDoctors} doctors, ${response.data.stats.totalHospitals} hospitals.`);
    } catch (error) {
      console.error('Optimization error:', error);
      alert('Failed to optimize database. Please try again.');
    } finally {
      setDataManagementLoading(false);
    }
  };

  if (loading) return (
    <div className="settings-container">
      <div className="loading-spinner">Loading settings...</div>
    </div>
  );

  if (error) return (
    <div className="settings-container">
      <div className="error-message">Error: {error}</div>
      <button onClick={loadData} className="retry-btn">Retry</button>
    </div>
  );

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2>System Settings</h2>
        <p>Manage application settings and view system information</p>
      </div>

      {authRequired && (
        <div className="login-panel">
          <div className="login-panel-header">
            <div className="login-panel-icon icon-badge icon-badge--md">
              <AppIcon name="lock" size={26} strokeWidth={2.25} className="app-icon--white" />
            </div>
            <div>
              <h3>Admin Login Required</h3>
              <p>Sign in to manage groups and admin settings</p>
            </div>
          </div>
          <div className="login-hint">
            Group management requires login. Default credentials are <strong>admin</strong> / <strong>admin123</strong> unless changed on the server.
          </div>
          <form onSubmit={handleLogin} className="login-form">
            <div className="login-form-row">
              <div className="form-group">
                <label htmlFor="login-username">Username</label>
                <input
                  id="login-username"
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  disabled={authLoading}
                  autoComplete="username"
                  placeholder="Enter username"
                />
              </div>
              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  disabled={authLoading}
                  autoComplete="current-password"
                  placeholder="Enter password"
                />
              </div>
            </div>
            <button type="submit" disabled={authLoading} className="login-submit-btn">
              {authLoading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      )}

      {/* System Overview */}
      <div className="system-overview">
        <h3 className="icon-with-label">
          <AppIcon name="overview" size={22} strokeWidth={2.25} />
          System Overview
        </h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon icon-badge icon-badge--md">
              <AppIcon name="hospitals" size={26} strokeWidth={2.25} />
            </div>
            <div className="stat-content">
              <h4>{systemStats.totalHospitals}</h4>
              <p>Total Hospitals</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon icon-badge icon-badge--md">
              <AppIcon name="doctors" size={26} strokeWidth={2.25} />
            </div>
            <div className="stat-content">
              <h4>{systemStats.totalDoctors}</h4>
              <p>Registered Doctors</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon icon-badge icon-badge--md">
              <AppIcon name="calendar" size={26} strokeWidth={2.25} />
            </div>
            <div className="stat-content">
              <h4>{systemStats.totalShifts}</h4>
              <p>Total Shifts</p>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon icon-badge icon-badge--md">
              <AppIcon name="location" size={26} strokeWidth={2.25} />
            </div>
            <div className="stat-content">
              <h4>{systemStats.activeLocations}</h4>
              <p>Active Locations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Admin / Groups */}
      <div className="settings-section">
        <h3>🛡️ Admin</h3>
        <div className="settings-grid">
          <div className="setting-card">
            <h4>Active Group</h4>
            <div className="setting-item">
              <label>Group</label>
              <select
                className="setting-input"
                value={activeGroupId}
                onChange={(e) => handleSelectGroup(e.target.value)}
              >
                {(groups || []).map((g) => (
                  <option key={g.id} value={String(g.id)}>
                    {g.name} ({g.slug})
                  </option>
                ))}
              </select>
            </div>
            <small>All tabs will show data for the selected group.</small>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="action-btn" type="button" onClick={handleRenameGroup}>
                Rename
              </button>
              <button className="action-btn" type="button" onClick={handleDeleteGroup}>
                Deactivate
              </button>
            </div>
          </div>

          <div className="setting-card">
            <h4>Create Group</h4>
            <form onSubmit={handleCreateGroup}>
              <div className="setting-item">
                <label>Group Slug</label>
                <input
                  className="setting-input"
                  value={newGroup.slug}
                  onChange={(e) => setNewGroup({ ...newGroup, slug: e.target.value })}
                  placeholder="e.g. mease-dunedin"
                />
              </div>
              <div className="setting-item">
                <label>Group Name</label>
                <input
                  className="setting-input"
                  value={newGroup.name}
                  onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                  placeholder="e.g. Mease Dunedin Hospital Group"
                />
              </div>
              <button className="action-btn" type="submit">
                Create Group
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Delete */}
      <div className="settings-section">
        <h3>🗑️ Delete</h3>
        <div className="settings-grid">
          <div className="setting-card">
            <h4>Delete schedules in active group</h4>
            <p>This will remove all shifts/events for the selected group.</p>
            <button className="action-btn" type="button" onClick={handleDeleteSchedulesForGroup}>
              Delete All Schedules (Group)
            </button>
          </div>
        </div>
      </div>

      {/* Application Settings */}
      <div className="settings-section">
        <h3 className="icon-with-label">
          <AppIcon name="settings" size={22} strokeWidth={2.25} />
          Application Settings
        </h3>
        <div className="settings-grid">
          
          {/* Schedule Settings */}
          <div className="setting-card">
            <h4 className="icon-with-label">
              <AppIcon name="schedule" size={20} strokeWidth={2} />
              Schedule Settings
            </h4>
            <div className="setting-item">
              <label>Default Shift Duration</label>
              <select defaultValue="24" className="setting-input">
                <option value="8">8 Hours</option>
                <option value="12">12 Hours</option>
                <option value="24">24 Hours</option>
              </select>
            </div>
            <div className="setting-item">
              <label>Auto-refresh Interval</label>
              <select defaultValue="30" className="setting-input">
                <option value="15">15 seconds</option>
                <option value="30">30 seconds</option>
                <option value="60">1 minute</option>
              </select>
            </div>
            <div className="setting-item">
              <label>Conflict Detection</label>
              <div className="toggle-switch">
                <input type="checkbox" id="conflict-detection" defaultChecked />
                <label htmlFor="conflict-detection"></label>
                    </div>
                  </div>
                </div>

          {/* AI Settings */}
          <div className="setting-card">
            <h4 className="icon-with-label">
              <AppIcon name="ai" size={20} strokeWidth={2} />
              AI Settings
            </h4>
            <div className="setting-item">
              <label>AI Schedule Generation</label>
              <div className="toggle-switch">
                <input type="checkbox" id="ai-generation" defaultChecked />
                <label htmlFor="ai-generation"></label>
              </div>
            </div>
            <div className="setting-item">
              <label>Preference Weight</label>
              <input type="range" min="1" max="10" defaultValue="7" className="setting-slider" />
              <span className="slider-value">7</span>
            </div>
            <div className="setting-item">
              <label>Balance Weight</label>
              <input type="range" min="1" max="10" defaultValue="8" className="setting-slider" />
              <span className="slider-value">8</span>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="setting-card">
            <h4>🔔 Notification Settings</h4>
            <div className="setting-item">
              <label>Email Notifications</label>
              <div className="toggle-switch">
                <input type="checkbox" id="email-notifications" />
                <label htmlFor="email-notifications"></label>
              </div>
            </div>
            <div className="setting-item">
              <label>Shift Reminders</label>
              <div className="toggle-switch">
                <input type="checkbox" id="shift-reminders" defaultChecked />
                <label htmlFor="shift-reminders"></label>
              </div>
            </div>
            <div className="setting-item">
              <label>Conflict Alerts</label>
              <div className="toggle-switch">
                <input type="checkbox" id="conflict-alerts" defaultChecked />
                <label htmlFor="conflict-alerts"></label>
              </div>
            </div>
          </div>

          {/* Display Settings */}
          <div className="setting-card">
            <h4>🎨 Display Settings</h4>
            <div className="setting-item">
              <label>Theme</label>
              <select
                value={theme}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'auto') {
                    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
                    setTheme(prefersDark ? 'dark' : 'light');
                    return;
                  }
                  setTheme(v);
                }}
                className="setting-input"
              >
                <option value="light">Light Theme</option>
                <option value="dark">Dark Theme</option>
                <option value="auto">Auto</option>
              </select>
            </div>
            <div className="setting-item">
              <label>Calendar View</label>
              <select value={calendarView} onChange={(e) => setCalendarView(e.target.value)} className="setting-input">
                <option value="month">Month View</option>
                <option value="week">Week View</option>
              </select>
            </div>
            <div className="setting-item">
              <label>Show Weekends</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="show-weekends"
                  checked={showWeekends}
                  onChange={(e) => setShowWeekends(e.target.checked)}
                />
                <label htmlFor="show-weekends"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="settings-section">
        <h3>💾 Data Management</h3>
        <div className="data-management">
          <div className="data-card">
            <h4>📊 Export Data</h4>
            <p>Export your schedule data in various formats</p>
            <div className="button-group">
              <button 
                className="export-btn" 
                onClick={() => handleExport('csv')}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Exporting...' : '📄 Export as CSV'}
              </button>
              <button 
                className="export-btn" 
                onClick={() => handleExport('json')}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Exporting...' : '📊 Export as JSON'}
              </button>
                                <button
                className="export-btn" 
                onClick={() => handleExport('pdf')}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Exporting...' : '📋 Export as PDF'}
                                </button>
                                <button
                className="export-btn" 
                onClick={() => handleExport('docx')}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Exporting...' : '📝 Export as Word'}
                                </button>
            </div>
          </div>
          
          <div className="data-card">
            <h4>🔄 Backup & Restore</h4>
            <p>Backup your data or restore from previous backups</p>
            <div className="button-group">
                                <button
                className="backup-btn" 
                onClick={handleCreateBackup}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Creating...' : '💾 Create Backup'}
                                </button>
                                <button
                className="restore-btn" 
                onClick={handleRestoreBackup}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Restoring...' : '📥 Restore Backup'}
                                </button>
            </div>
                </div>
          
          <div className="data-card">
            <h4>🗑️ Data Cleanup</h4>
            <p>Clean up old data and optimize performance</p>
            <div className="button-group">
              <button 
                className="cleanup-btn" 
                onClick={handleCleanOldShifts}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Cleaning...' : '🧹 Clean Old Shifts'}
              </button>
              <button 
                className="cleanup-btn" 
                onClick={handleOptimizeDatabase}
                disabled={dataManagementLoading}
              >
                {dataManagementLoading ? '⏳ Optimizing...' : '⚡ Optimize Database'}
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* System Information */}
      <div className="settings-section">
        <h3>ℹ️ System Information</h3>
        <div className="system-info">
          <div className="info-card">
            <div className="info-icon">🔧</div>
            <div className="info-content">
              <h4>API Status</h4>
              <p className="status-online">{systemStats.apiStatus}</p>
                </div>
              </div>
          
          <div className="info-card">
            <div className="info-icon">📦</div>
            <div className="info-content">
              <h4>Version</h4>
              <p>{systemStats.version}</p>
            </div>
                </div>
          
          <div className="info-card">
            <div className="info-icon">🌐</div>
            <div className="info-content">
              <h4>Environment</h4>
              <p>Production</p>
            </div>
                </div>
          
          <div className="info-card">
            <div className="info-icon">⏰</div>
            <div className="info-content">
              <h4>Last Updated</h4>
              <p>{new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="settings-section">
        <h3 className="icon-with-label">
          <AppIcon name="optimize" size={22} strokeWidth={2.25} />
          Quick Actions
        </h3>
        <div className="quick-actions">
          <button type="button" className="action-btn icon-with-label" onClick={() => { window.location.hash = '#schedule'; }}>
            <AppIcon name="schedule" size={20} strokeWidth={2.25} />
            Go to Schedule
          </button>
          <button type="button" className="action-btn icon-with-label" onClick={() => { window.location.hash = '#doctors'; }}>
            <AppIcon name="doctors" size={20} strokeWidth={2.25} />
            Manage Doctors
          </button>
          <button type="button" className="action-btn icon-with-label" onClick={() => { window.location.hash = '#hospitals'; }}>
            <AppIcon name="hospitals" size={20} strokeWidth={2.25} />
            Manage Hospitals
          </button>
          <button type="button" className="action-btn icon-with-label" onClick={() => { window.location.hash = '#availability'; }}>
            <AppIcon name="availability" size={20} strokeWidth={2.25} />
            Check Availability
          </button>
          <button type="button" className="action-btn icon-with-label" onClick={() => { window.location.hash = '#analytics'; }}>
            <AppIcon name="analytics" size={20} strokeWidth={2.25} />
            View Analytics
          </button>
        </div>
      </div>

      <div className="last-updated">
        Last updated: {new Date().toLocaleString()}
      </div>
    </div>
  );
};

export default Settings;


